import { useMemo } from "react";
import { useAuthenticated } from "../auth";
import { portalServer } from "../config";
import {
  useSyncData,
  useSyncModuleRegistration,
  type SyncModule,
} from "../sync";
import type { Sperrung } from "../types";
import { limiter } from "./demo";
import { daily } from "./provider";

const syncModuleId = "sperrungen";

function useSperrungenSyncModule(): SyncModule<ReadonlyArray<Sperrung>> {
  const { portalKey, demo } = useAuthenticated();

  return useMemo(
    () => ({
      id: syncModuleId,
      i18nKey: `karte:tasks.${syncModuleId}`,
      defaultOptions: {
        schedule: { type: "periodic", interval: daily },
      },
      fetchData: async () => {
        const response = await fetch(
          `${portalServer}/get-blocks.php?ORG_ACCESSKEY=${encodeURIComponent(
            portalKey,
          )}`,
        );

        if (!response.ok) {
          throw new Error(
            `Error fetching data: ${response.status} ${response.statusText}`,
          );
        }

        const data = await response.json();
        const limited = limiter<Sperrung>(demo, 3);
        return limited(data.BLOCKS_LIST ?? []).map(parseSperrung);
      },
    }),
    [demo, portalKey],
  );
}

export function useSperrungenSync() {
  const module = useSperrungenSyncModule();
  useSyncModuleRegistration(module);
}

export function useSperrungen(): ReadonlyArray<Sperrung> {
  return useSyncData(syncModuleId) ?? [];
}

function parseSperrung(entry: any): Sperrung {
  return {
    id: entry.BLOCK_ID,
    latitude: parseFloat(entry.BLOCK_GEO_LAT),
    longitude: parseFloat(entry.BLOCK_GEO_LNG),
    typ: "sperrung",
    sperrungTyp: "Vollsperrung",
    icon: "sperrung",
    color: "#a22717",
    dashArray: "10 10",
    name: entry.BLOCK_TEXT,
    ort: entry.BLOCK_PLACE,
    von: entry.BLOCK_TIME_FROM,
    bis: entry.BLOCK_TIME_TO,
    beschreibung: `${entry.BLOCK_PLACE}\nvon ${entry.BLOCK_TIME_FROM} bis ${entry.BLOCK_TIME_TO}`,
    segments:
      Array.isArray(entry.BLOCK_ARRAY) && entry.BLOCK_ARRAY.length > 1
        ? entry.BLOCK_ARRAY.map((seg: string[]) => seg.map(parseFloat))
        : [],
    ...parseSperrungData(entry.BLOCK_TYPE),
  };
}

function parseSperrungData(typ: string): Partial<Sperrung> {
  switch (typ) {
    case "1":
      return { sperrungTyp: "Teilsperrung", icon: "sperrung" };
    case "2":
      return { sperrungTyp: "Information", icon: "sperrung" };
    case "3":
      return {
        sperrungTyp: "Bedingt Befahrbar",
        icon: "bedingtbefahrbar",
        color: "#3d5afe",
      };
    case "4":
      return {
        sperrungTyp: "Behelfsweg",
        icon: "behelfsweg",
        color: "#4caf50",
      };
    case "5":
      return {
        sperrungTyp: "Befahrbarer Weg",
        icon: "befahrbarerweg",
        color: "#007F0E",
        dashArray: "",
        opacity: 0.5,
      };
    default:
      return {};
  }
}
